import moment from "moment";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_SPAN_DAYS = 366;

export function coerceEventMoment(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    const m = moment.utc(value);
    return m.isValid() ? m : null;
  }
  if (typeof value.toDate === "function") {
    return coerceEventMoment(value.toDate());
  }
  if (typeof value.seconds === "number") {
    return coerceEventMoment(new Date(value.seconds * 1000));
  }
  const m = moment.utc(value);
  return m.isValid() ? m : null;
}

export function eventStartDate(event) {
  if (!event || !event.start) return null;
  if (typeof event.start === "string") {
    const date = event.start.slice(0, 10);
    return ISO_DATE.test(date) ? date : null;
  }
  const m = coerceEventMoment(event.start);
  if (!m) return null;
  const date = m.format("YYYY-MM-DD");
  return ISO_DATE.test(date) ? date : null;
}

/**
 * Inclusive occupancy for a UTC calendar day.
 * Classic all-day drags use FullCalendar's exclusive midnight end
 * (Mon 00:00 → Thu 00:00 covers Mon–Wed, not Thu). V2 leave uses
 * 00:00–23:59, which covers that one day only.
 */
export function eventOccupiesDate(event, date) {
  if (!event || !ISO_DATE.test(date)) return false;
  const start = coerceEventMoment(event.start);
  if (!start) return false;
  const dayStart = moment.utc(date, "YYYY-MM-DD", true);
  if (!dayStart.isValid()) return false;
  const dayEnd = dayStart.clone().add(1, "day");
  if (!start.isBefore(dayEnd)) return false;
  const end = coerceEventMoment(event.end);
  if (!end || !end.isAfter(start)) {
    return start.format("YYYY-MM-DD") === date;
  }
  return end.isAfter(dayStart);
}

export function eventOccupiedDates(event) {
  const startDate = eventStartDate(event);
  if (!startDate) return [];
  if (!eventOccupiesDate(event, startDate)) return [];
  const dates = [startDate];
  let current = startDate;
  for (let i = 0; i < MAX_SPAN_DAYS - 1; i += 1) {
    const next = moment
      .utc(current, "YYYY-MM-DD", true)
      .add(1, "day")
      .format("YYYY-MM-DD");
    if (!ISO_DATE.test(next) || next <= current) break;
    if (!eventOccupiesDate(event, next)) break;
    dates.push(next);
    current = next;
  }
  return dates;
}

export function isMultiDayEvent(event) {
  return eventOccupiedDates(event).length > 1;
}

export function uniqueRosterEvents(events) {
  const out = [];
  const seen = new Set();
  (events || []).forEach(event => {
    if (!event) return;
    if (event._id) {
      if (seen.has(event._id)) return;
      seen.add(event._id);
    }
    out.push(event);
  });
  return out;
}

export function minutesOnDate(event, date) {
  const start = coerceEventMoment(event && event.start);
  const end = coerceEventMoment(event && event.end);
  if (!start || !end || !ISO_DATE.test(date)) return 0;
  const dayStart = moment.utc(date, "YYYY-MM-DD", true);
  if (!dayStart.isValid()) return 0;
  const dayEnd = dayStart.clone().add(1, "day");
  const clipStart = moment.max(start, dayStart);
  const clipEnd = moment.min(end, dayEnd);
  if (!clipEnd.isAfter(clipStart)) return 0;
  return clipEnd.diff(clipStart, "minutes");
}

export function clippedTimeLabels(event, date) {
  const start = coerceEventMoment(event && event.start);
  const end = coerceEventMoment(event && event.end);
  if (!start || !ISO_DATE.test(date)) return null;
  const dayStart = moment.utc(date, "YYYY-MM-DD", true);
  if (!dayStart.isValid()) return null;
  const dayEnd = dayStart.clone().add(1, "day");
  const rangeEnd = end && end.isAfter(start) ? end : start.clone().add(1, "minute");
  const clipStart = moment.max(start, dayStart);
  const clipEnd = moment.min(rangeEnd, dayEnd);
  if (!clipEnd.isAfter(clipStart)) return null;
  const startLabel = clipStart.format("HH:mm");
  let endLabel = clipEnd.format("HH:mm");
  if (endLabel === "00:00" && clipEnd.format("YYYY-MM-DD") !== date) {
    endLabel = "23:59";
  }
  return { start: startLabel, end: endLabel };
}
